/* eslint-disable i18next/no-literal-string */
// src/components/errors/ErrorsTable.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Button,
  useTheme,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { tokens } from '../../../theme';

const HTTP_STATUS_COLORS = {
  400: 'warning',
  401: 'error',
  403: 'error',
  404: 'info',
  422: 'warning',
  500: 'error',
  502: 'error',
  503: 'error',
};

export default function ErrorsTable({
  errors = [],
  loading = false,
  currentView = 'overview',
  errorSummary = null,
  onErrorClick,
  onRefresh,
  onLoadDetailedErrors,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (statusCode) => {
    return HTTP_STATUS_COLORS[statusCode] || 'default';
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const paginatedErrors = errors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && errors.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Error Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Error Code</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={200} /></TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={60} /></TableCell>
                    <TableCell><Skeleton width={40} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  }

  if (errors.length === 0) {
    // Special handling for overview mode with error summary
    if (currentView === 'overview' && errorSummary && errorSummary.total_errors > 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>
              {errorSummary.total_errors} errors detected
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Select a specific view or endpoint to see detailed error information.
            </Typography>
            
            {/* Show top endpoints as buttons */}
            {errorSummary.errors_by_endpoint && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Top Error-Prone Endpoints:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center">
                  {Object.entries(errorSummary.errors_by_endpoint)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 3)
                    .map(([endpoint, count]) => (
                      <Button
                        key={endpoint}
                        variant="outlined"
                        size="small"
                        onClick={() => onLoadDetailedErrors('endpoint', endpoint)}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {endpoint} ({count})
                      </Button>
                    ))}
                </Box>
              </Box>
            )}
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
            >
              Load Recent Errors
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No errors found
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            {currentView === 'overview' 
              ? "No errors detected in the selected time range."
              : "No errors match your current filters."
            }
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
          >
            Refresh Data
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            Error Details ({errors.length} total)
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Occurred</TableCell>
                <TableCell>Error Code</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>HTTP Status</TableCell>
                <TableCell>Staff Member</TableCell>
                <TableCell>Department</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedErrors.map((error) => (
                <TableRow 
                  key={error.error_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onErrorClick(error)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        <LocalizedDateDistance date={error.occurred_at} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(error.occurred_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={error.error_code}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Tooltip title={error.error_message} arrow>
                      <Typography variant="body2">
                        {truncateText(error.error_message, 60)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontFamily="monospace"
                        fontSize="0.8rem"
                        color="primary.main"
                      >
                        {error.http_method}
                      </Typography>
                      <Tooltip title={error.endpoint} arrow>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          fontFamily="monospace"
                        >
                          {truncateText(error.endpoint, 30)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={error.http_status_code}
                      size="small"
                      color={getStatusColor(error.http_status_code)}
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {error.staff_member_id ? (
                      <Typography variant="body2">
                        Staff ID: {error.staff_member_id.substring(0, 8)}...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Anonymous
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {error.department_id ? (
                      <Typography variant="body2">
                        Dept ID: {error.department_id.substring(0, 8)}...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Unknown
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onErrorClick(error);
                        }}
                        sx={{
                          color: colors.blueAccent[500],
                          '&:hover': {
                            backgroundColor: colors.blueAccent[900],
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={errors.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
}